/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { BarChart, BarChartWithLine, ExpandButton } from '@components';
import { trimSpace, formatFirstCharacter } from '@utils/widget';
import {
  SwitchConfigInChart,
  reflowChart,
  destroyCharts,
  notPercentValue,
} from './GrossProfit';
import D_Number from '@utils/number';
import {
  getValueFromSearchParam,
  removeValueFromSearchParam,
  setURLSearchParam,
} from '@utils/history';
import searchParamKeys from '@model/searchParamKeys';
import { useHistory } from 'react-router-dom';
const OperatingIncome = ({
  title,
  condition,
  others: data = {},
  buyerManualInputFlag,
  targetManualInputFlag,
}) => {
  const { expand: initialExpand } = getValueFromSearchParam(
    searchParamKeys.expand,
  );
  const [choosedTimeline, setChoosedTimeline] = useState('ltmForQuarter');
  const [choosedMetrics, setChoosedMetrics] = useState('');
  const [categories, setCategories] = useState([]);
  const [series, setSeries] = useState([]);
  const [expand, setExpand] = useState(initialExpand);
  const [chartCondition, setChartCondition] = useState(condition);
  const barRefs = [useRef(null), useRef(null)];
  const barLineRefs = [useRef(null), useRef(null)];
  const [barYMax, setBarYMax] = useState(0);
  const [barYMin, setBarYMin] = useState(0);
  const [barLineYMax, setBarLineYMax] = useState({
    leftMax: 0,
    rightMax: 0,
    leftMin: 0,
    rightMin: 0,
  });
  const handleMetricsWhenChooseTimeline = (timeline) => {
    if (timeline === 'ltmForQuarter' || timeline === '1yForQuarter') {
      setChartCondition({
        Timeline: condition.Timeline,
        Metrics: condition.Metrics.filter((m) => m.value !== 'eps'),
      });
    } else {
      setChartCondition(condition);
    }
  };
  const swichConditionHandler = (type, value) => {
    if (type === 'Timeline') {
      if (value === choosedTimeline) {
        return;
      }
      setChoosedMetrics('');
      setChoosedTimeline(value);
    } else {
      if (value === choosedMetrics) {
        return;
      }
      setChoosedMetrics(value);
    }
    setCategories([]);
    setSeries([]);
  };

  // const buildCategory = (item) => {
  //   const year = new Date(item.date).getFullYear();
  //   return `FY, ${year}`;
  // };

  const buildCategories = () => {
    const targets = Object.keys(data);
    const categories = targets.map((target) => {
      const operatingIncomes =
        data[target][choosedTimeline].operatingIncomeList;
      const values = operatingIncomes.map(
        (operatingIncome) => operatingIncome.displayEndDate,
      );
      return { [target]: values };
    });

    setCategories(categories);
  };

  const buildSeries = () => {
    const targets = Object.keys(data);
    const series = targets.map((target) => {
      const seryList = [];
      if (choosedMetrics === '') {
        const operatingIncomes =
          data[target][choosedTimeline].operatingIncomeList;
        const values = operatingIncomes.map(
          (operatingIncome) => operatingIncome.value,
        );
        const unitType = data[target][choosedTimeline].yAxis.operatingIncome;
        operatingIncomes[0] &&
          seryList.push({
            name: 'Operating Income',
            data: values,
            unitType,
          });
      } else {
        // timeline
        const operatingIncomes =
          data[target][choosedTimeline].operatingIncomeList;
        const values = operatingIncomes.map(
          (operatingIncome) => operatingIncome.value,
        );
        const unitType = data[target][choosedTimeline].yAxis.operatingIncome;
        operatingIncomes[0] &&
          seryList.push({
            name: 'Operating Income',
            data: values,
            unitType,
          });
        // metics
        const metricsKey = trimSpace(choosedMetrics);
        const metrics = data[target][choosedTimeline].metrics[metricsKey];
        const metricsValues = operatingIncomes.map((operatingIncome) => null);
        metrics.map((metric, idx) => {
          const metricIdx = operatingIncomes.findIndex(
            (operatingIncome) =>
              operatingIncome.displayEndDate === metric.displayEndDate,
          );
          metricsValues[metricIdx] = metric.value;
          return metric.value;
        });
        metricsValues.find((m) => m != null && m !== '') &&
          seryList.push({
            name:
              choosedMetrics === 'eps'
                ? 'EPS'
                : formatFirstCharacter(choosedMetrics),
            data: notPercentValue.includes(choosedMetrics)
              ? metricsValues
              : metricsValues.map((v) => (v === 0 ? null : v)),
            unitType: data[target][choosedTimeline].yAxis[metricsKey],
          });
      }

      return { [target]: seryList };
    });

    setSeries(series);
  };

  useEffect(() => {
    buildCategories();
    buildSeries();
    handleMetricsWhenChooseTimeline(choosedTimeline);
  }, [choosedTimeline, choosedMetrics, data]);

  useEffect(() => {
    reflowChart(...barRefs, ...barLineRefs);
  }, [expand]);

  useEffect(
    () => () => {
      destroyCharts(...barRefs, ...barLineRefs);
    },
    [],
  );
  // ✅ Fixed version for single yAxis charts (BarChart)
  useEffect(() => {
    try {
      const validBarRefs = barRefs.filter((ref) => {
        return ref?.current?.yAxis?.[0];
      });

      if (validBarRefs.length > 0) {
        const barMax = validBarRefs.map((c) => c.current.yAxis[0].max);
        const barMin = validBarRefs.map((c) => c.current.yAxis[0].min);

        const barYAxisMax = D_Number.getArrayMaxNumber(barMax);
        const barYAxisMin = D_Number.getArrayMinNumber(barMin);

        setBarYMax(barYAxisMax);
        setBarYMin(barYAxisMin);
      }
    } catch (error) {
      console.error('Error processing OperatingIncome bar chart yAxis:', error);
    }
  }, [barRefs]);

  // ✅ Fixed version for dual yAxis charts (BarChartWithLine)
  useEffect(() => {
    try {
      const validBarLineRefs = barLineRefs.filter((ref) => {
        return ref?.current?.yAxis?.[0] && ref?.current?.yAxis?.[1];
      });

      if (validBarLineRefs.length === 2) {
        // Ensure we have both charts
        const barLineMax = {
          leftMax: validBarLineRefs.map((c) => c.current.yAxis[0].max),
          rightMax: validBarLineRefs.map((c) => c.current.yAxis[1].max),
          leftMin: validBarLineRefs.map((c) => c.current.yAxis[0].min),
          rightMin: validBarLineRefs.map((c) => c.current.yAxis[1].min),
        };

        const barLineYAxisMax = {
          leftMax: D_Number.getArrayMaxNumber(barLineMax.leftMax),
          rightMax: D_Number.getArrayMaxNumber(barLineMax.rightMax),
          leftMin: D_Number.getArrayMinNumber(barLineMax.leftMin),
          rightMin: D_Number.getArrayMinNumber(barLineMax.rightMin),
        };

        if (
          barLineYAxisMax.leftMax !== barLineYMax.leftMax ||
          barLineYAxisMax.rightMax !== barLineYMax.rightMax ||
          barLineYAxisMax.leftMin !== barLineYMax.leftMin ||
          barLineYAxisMax.rightMin !== barLineYMax.rightMin
        ) {
          setBarLineYMax(barLineYAxisMax);
        }
      }
    } catch (error) {
      console.error(
        'Error processing OperatingIncome bar line chart yAxis:',
        error,
      );
    }
  }, [barLineRefs]);

  const history = useHistory();
  const onExpandClick = () => {
    setExpand(!expand);
    if (!expand) {
      setURLSearchParam(history, {
        [searchParamKeys.expand]: true,
      });
    } else {
      removeValueFromSearchParam(history, [searchParamKeys.expand]);
    }
  };

  return (
    <div className={`analysis-right-wrapper ${expand && 'full-screen'}`}>
      <div className="title expand-box">
        <div>{title}</div>
        {data && (
          <ExpandButton
            onClick={onExpandClick}
            scrollPageFooter
            initialExpand={initialExpand}
          />
        )}
      </div>
      <div className="common-chart-wrapper">
        {(buyerManualInputFlag && targetManualInputFlag) ||
        (data.buyer?.noDataFlag && data.target?.noDataFlag) ? null : (
          <div className="conditionContainer">
            <div className="conditionList">
              {Object.keys(chartCondition).map((k, idx) => (
                <SwitchConfigInChart
                  key={`${k}-${idx}`}
                  label={k}
                  options={chartCondition[k]}
                  defaultValue={
                    k === 'Timeline' ? choosedTimeline : choosedMetrics
                  }
                  switchCondition={swichConditionHandler}
                  value={k === 'Timeline' ? choosedTimeline : choosedMetrics}
                />
              ))}
            </div>
          </div>
        )}

        <div className="chartList">
          {Object.keys(data).map((target, idx, items) => {
            const isLast = items.length - 1 === idx;

            if (choosedMetrics !== '') {
              return (
                <>
                  <BarChartWithLine
                    ref={barLineRefs[idx]}
                    key={`${target}-${idx}`}
                    containerId={`bar-chart-${idx}`}
                    companyName={data[target].companyName}
                    noDataFlag={data[target].noDataFlag}
                    categories={
                      categories.length ? categories[idx][target] : []
                    }
                    series={series.length ? series[idx][target] : []}
                    metrics={choosedMetrics}
                    secondaryXAxis={
                      data[target][choosedTimeline]?.xAxis?.secondLine || null
                    }
                    yMax={barLineYMax}
                  />
                  {!isLast && <div className="line" />}
                </>
              );
            }
            return (
              <>
                <BarChart
                  ref={barRefs[idx]}
                  key={`${target}-${idx}`}
                  containerId={`bar-chart-${idx}`}
                  companyName={data[target].companyName}
                  noDataFlag={data[target].noDataFlag}
                  categories={categories.length ? categories[idx][target] : []}
                  series={series.length ? series[idx][target] : []}
                  secondaryXAxis={
                    data[target][choosedTimeline]?.xAxis?.secondLine || null
                  }
                  yMax={barYMax}
                  yMin={barYMin}
                />
                {!isLast && <div className="line" />}
              </>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default React.memo(OperatingIncome);
